import renderIcon from 'icon';
import { FiMinus, FiPlus } from 'react-icons/fi';

type IProductQtyCounter = {
  value: number;
  setValue: (arr: number) => void;
  customerType?: 'REGULAR' | 'BUSINESS';
  max?: number;
  minimumOrderQuantity?: number;
  disabled?: boolean;
};

export const ProductQtyCounter = ({
  value,
  setValue,
  customerType,
  max,
  minimumOrderQuantity,
  disabled = false
}: IProductQtyCounter) => {
  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    const minValue = customerType === 'BUSINESS' ? minimumOrderQuantity || 1 : 1;
    if (value > minValue) {
      setValue(value - 1);
    } else if (customerType !== 'BUSINESS') {
      // For regular customers, going below 1 removes the item
      setValue(0);
    }
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;

    if (max && value >= max) {
      return;
    }
    setValue(value + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    const newValue = Number(e.currentTarget.value);
    const minValue = customerType === 'BUSINESS' ? minimumOrderQuantity || 1 : 1;

    if (newValue >= minValue && (!max || newValue <= max)) {
      setValue(newValue);
    }
  };

  const minValue = customerType === 'BUSINESS' ? minimumOrderQuantity || 1 : 1;
  const isDecrementDisabled = disabled || (customerType === 'BUSINESS' && value <= minValue);
  const isIncrementDisabled = disabled || (max ? value >= max : false);

  return (
    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
      <button
        className={`outline-none text-sm p-0.5 rounded-full aspect-square border border-black/30 transition-all ${
          isDecrementDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-black/60 hover:bg-black/5'
        }`}
        onClick={handleDecrement}
        disabled={isDecrementDisabled}
        type="button">
        {renderIcon(FiMinus)}
      </button>

      <input
        data-business={customerType === 'BUSINESS'}
        min={minValue}
        max={max}
        step="1"
        type="number"
        disabled={customerType !== 'BUSINESS' || disabled}
        value={value}
        onChange={handleInputChange}
        onClick={e => e.stopPropagation()}
        className={`bg-transparent data-[business=true]:border border-black/10 hover:border-black/50 transition-all rounded py-1 w-10 text-center font-light text-sm outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
          disabled ? 'opacity-50' : ''
        }`}
      />

      <button
        className={`outline-none text-sm p-0.5 rounded-full aspect-square border border-black/30 transition-all ${
          isIncrementDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-black/60 hover:bg-black/5'
        }`}
        onClick={handleIncrement}
        disabled={isIncrementDisabled}
        type="button">
        {renderIcon(FiPlus)}
      </button>
    </div>
  );
};
